export const DEBT_LOGIN_FIELD_EXPERIMENT = 'debt-login-field-v1';
export const DEBT_LOGIN_FIELD_VARIANTS = {
  control: 'ssn4',
  treatment: 'lastname',
};

export const PARTIAL_PAYMENT_SELECTOR_EXPERIMENT = 'partial-payment-selector-v1';
export const PARTIAL_PAYMENT_SELECTOR_VARIANTS = {
  control: 'percentage',
  treatment: 'dollar',
};

// Internal registry describing every experiment the portal can evaluate. Each
// entry maps the public experiment key to its variant set and the fallback
// variant returned when the participant is not in a running treatment.
const EXPERIMENT_REGISTRY = {
  [DEBT_LOGIN_FIELD_EXPERIMENT]: {
    variants: DEBT_LOGIN_FIELD_VARIANTS,
    controlVariant: DEBT_LOGIN_FIELD_VARIANTS.control,
    fallbackVariant: DEBT_LOGIN_FIELD_VARIANTS.treatment,
  },
  [PARTIAL_PAYMENT_SELECTOR_EXPERIMENT]: {
    variants: PARTIAL_PAYMENT_SELECTOR_VARIANTS,
    controlVariant: PARTIAL_PAYMENT_SELECTOR_VARIANTS.control,
    fallbackVariant: PARTIAL_PAYMENT_SELECTOR_VARIANTS.treatment,
  },
};

// Preserve current behavior for unknown keys by defaulting to the debt-login
// control variant and its allowed variant set.
const getExperimentRegistryEntry = (experimentKey) =>
  EXPERIMENT_REGISTRY[experimentKey] || {
    variants: DEBT_LOGIN_FIELD_VARIANTS,
    controlVariant: DEBT_LOGIN_FIELD_VARIANTS.control,
    fallbackVariant: DEBT_LOGIN_FIELD_VARIANTS.control,
  };
const DEFAULT_TIMEOUT_MS = 7000;
const activeAssignments = new Map();

const normalizeParticipantKey = (participant) => {
  const value =
    participant && typeof participant === 'object'
      ? participant.debtId
      : participant;
  return String(value || '').trim() || null;
};

const trimTrailingSlash = (value) =>
  String(value || '')
    .trim()
    .replace(/\/+$/, '');

export const resolveApiV2BaseUrl = () => {
  const explicit = trimTrailingSlash(process.env.API_V2_URL);
  if (explicit) return explicit;

  const apiUrl = trimTrailingSlash(process.env.API_URL);
  if (!apiUrl) return '/api/v2';
  if (/\/api\/v1$/i.test(apiUrl)) {
    return apiUrl.replace(/\/api\/v1$/i, '/api/v2');
  }
  if (/\/api\/v2$/i.test(apiUrl)) {
    return apiUrl;
  }
  try {
    const parsed = new URL(apiUrl);
    if (
      ['http:', 'https:'].includes(parsed.protocol) &&
      parsed.pathname === '/' &&
      !parsed.search &&
      !parsed.hash
    ) {
      return `${apiUrl}/api/v2`;
    }
  } catch (_error) {
    // Fall through to the same-origin proxy for malformed or relative values.
  }
  return '/api/v2';
};

export const createExperimentRunId = (assignment) => {
  if (assignment?.experimentRunId) return assignment.experimentRunId;
  if (
    assignment?.experimentDefinitionId &&
    Number.isInteger(assignment?.run)
  ) {
    return `appconfig:${assignment.experimentDefinitionId}:${assignment.run}`;
  }
  return null;
};

export const createControlFallback = (experimentKey) => {
  const { controlVariant, fallbackVariant } =
    getExperimentRegistryEntry(experimentKey);

  return {
    experimentKey,
    experimentDefinitionId: null,
    run: null,
    treatmentKey: null,
    variant: fallbackVariant,
    isControl: fallbackVariant === controlVariant,
    participating: false,
    assignmentId: null,
    assignmentToken: null,
    assignedAt: null,
    expiresAt: null,
  };
};

export const createSessionAssignmentDescriptor = (
  assignment,
  portalSessionId,
) => ({
  portalSessionId: portalSessionId || null,
  assignmentId: assignment?.assignmentId || null,
  experimentKey: assignment?.experimentKey || null,
  experimentRunId: createExperimentRunId(assignment),
  variantKey: assignment?.variant || null,
  participating: assignment?.participating === true,
  assignmentToken: assignment?.assignmentToken || null,
  assignedAt: assignment?.assignedAt || new Date().toISOString(),
  expiresAt: assignment?.expiresAt || null,
});

export const restoreAssignmentDescriptor = (
  descriptor,
  { experimentKey, portalSessionId },
) => {
  const registryEntry = EXPERIMENT_REGISTRY[experimentKey];
  const allowedVariants = registryEntry
    ? Object.values(registryEntry.variants)
    : [];
  const assignmentId = String(descriptor?.assignmentId || '').trim();
  const assignmentToken = String(descriptor?.assignmentToken || '').trim();
  const expiresAt = String(descriptor?.expiresAt || '').trim();
  const expiresAtMs = Date.parse(expiresAt);

  if (
    descriptor?.experimentKey !== experimentKey ||
    descriptor?.portalSessionId !== portalSessionId ||
    descriptor?.participating !== true ||
    !assignmentId ||
    !assignmentToken ||
    !allowedVariants.includes(descriptor?.variantKey) ||
    !Number.isFinite(expiresAtMs) ||
    expiresAtMs <= Date.now()
  ) {
    return null;
  }

  return {
    experimentKey,
    experimentRunId: descriptor.experimentRunId || null,
    variant: descriptor.variantKey,
    participating: true,
    isControl: descriptor.variantKey === registryEntry.controlVariant,
    assignmentId,
    assignmentToken,
    assignedAt: descriptor.assignedAt || null,
    expiresAt,
  };
};

export const setActiveExperimentAssignment = (assignment, participant = null) => {
  if (!assignment?.experimentKey) return;
  activeAssignments.set(assignment.experimentKey, {
    ...assignment,
    experimentRunId: createExperimentRunId(assignment),
    participantKey: normalizeParticipantKey(participant),
  });
};

export const clearActiveExperimentAssignment = (experimentKey) => {
  activeAssignments.delete(experimentKey);
};

export const getActiveExperimentAssignment = (
  experimentKey,
  participant = null,
) => {
  const assignment = activeAssignments.get(experimentKey) || null;
  if (
    assignment?.expiresAt &&
    new Date(assignment.expiresAt).getTime() <= Date.now()
  ) {
    activeAssignments.delete(experimentKey);
    return null;
  }
  const participantKey = normalizeParticipantKey(participant);
  if (participantKey && assignment?.participantKey !== participantKey) {
    return null;
  }
  return assignment;
};

export const clearActiveExperimentAssignments = () => {
  activeAssignments.clear();
};

export const evaluateExperiment = async ({
  experimentKey,
  participant,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}) => {
  const unavailableFallback = {
    ...createControlFallback(experimentKey),
    evaluationUnavailable: true,
  };
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(
      `${resolveApiV2BaseUrl()}/experiments/evaluate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(process.env.API_KEY ? { 'x-api-key': process.env.API_KEY } : {}),
        },
        body: JSON.stringify({
          experiments: [
            {
              key: experimentKey,
              participant,
            },
          ],
        }),
        signal: controller.signal,
      },
    );
    if (!response.ok) return unavailableFallback;
    const payload = await response.json().catch(() => ({}));
    const assignment = payload?.data?.assignments?.[0];
    const registryEntry = EXPERIMENT_REGISTRY[experimentKey];
    const allowedVariants = registryEntry
      ? Object.values(registryEntry.variants)
      : null;
    if (
      assignment?.experimentKey !== experimentKey ||
      typeof assignment?.variant !== 'string' ||
      !assignment.variant ||
      (allowedVariants && !allowedVariants.includes(assignment.variant))
    ) {
      return unavailableFallback;
    }
    return assignment;
  } catch (_error) {
    return unavailableFallback;
  } finally {
    clearTimeout(timeoutId);
  }
};
